import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Kunde, PoolMasse, PoolzeichnungRequest, Seite } from '../types';
import kundeService from '../services/kundeService';
import poolzeichnungService from '../services/poolzeichnungService';
import { Icon, PageHeader, LoadingScreen, Spinner, fehlerText, filterAktiv } from '../components/ui';
import { ZurueckLink } from '../components/ZurueckLink';
import { markiereNeu } from '../utils/ankunft';
import { DatumFeld } from '../components/DatumZeit';

const heute = () => new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD in Ortszeit

/** Eingaben bleiben Text, damit Felder leer sein dürfen; erst beim Senden werden es ganze mm. */
type Teil = { aktiv: boolean; breite: string; tiefe: string; seite: Seite };
type Form = {
  kundeId: number;
  datum: string;
  titel: string;
  innenLaenge: string; innenBreite: string; innenHoehe: string;
  aussenLaenge: string; aussenBreite: string;
  skimmer: Teil;
  gegenstromanlage: Teil;
  grube: { oben: string; unten: string; links: string; rechts: string };
  schichten: { poolboden: string; isolierung: string; bodenplatte: string; drainage: string; umrandung: string; ueberstand: string };
  sichtbar: PoolMasse['sichtbar'];
};
type TeilName = 'skimmer' | 'gegenstromanlage';

const mm = (s: string) => Math.max(0, Math.round(Number(s.replace(',', '.'))) || 0);
const text = (n: number | undefined, leerBeiNull = false) => (n == null || (leerBeiNull && n === 0) ? '' : String(n));
const anderes = (t: TeilName): TeilName => (t === 'skimmer' ? 'gegenstromanlage' : 'skimmer');
const gegenueber = (s: Seite): Seite => (s === 'RECHTS' ? 'LINKS' : 'RECHTS');

const leer = (kundeId: number): Form => ({
  kundeId,
  datum: heute(),
  titel: 'Poolzeichnung',
  innenLaenge: '', innenBreite: '', innenHoehe: '',
  aussenLaenge: '', aussenBreite: '',
  skimmer: { aktiv: false, breite: '', tiefe: '', seite: 'RECHTS' },
  gegenstromanlage: { aktiv: false, breite: '', tiefe: '', seite: 'LINKS' },
  grube: { oben: '200', unten: '200', links: '200', rechts: '200' },
  schichten: { poolboden: '', isolierung: '', bodenplatte: '200', drainage: '250', umrandung: '', ueberstand: '' },
  sichtbar: { innen: true, aussen: true, grube: true },
});

const ausMasse = (kundeId: number, datum: string, titel: string, m: PoolMasse): Form => {
  const teil = (t: PoolMasse['skimmer'], standard: Seite): Teil =>
    t ? { aktiv: true, breite: text(t.breite), tiefe: text(t.tiefe), seite: t.seite }
      : { aktiv: false, breite: '', tiefe: '', seite: standard };
  return {
    kundeId, datum, titel,
    innenLaenge: text(m.innenLaenge), innenBreite: text(m.innenBreite), innenHoehe: text(m.innenHoehe),
    aussenLaenge: text(m.aussenLaenge), aussenBreite: text(m.aussenBreite),
    skimmer: teil(m.skimmer, 'RECHTS'),
    gegenstromanlage: teil(m.gegenstromanlage, 'LINKS'),
    grube: { oben: text(m.grube.oben), unten: text(m.grube.unten), links: text(m.grube.links), rechts: text(m.grube.rechts) },
    schichten: {
      poolboden: text(m.schichten.poolboden, true), isolierung: text(m.schichten.isolierung, true),
      bodenplatte: text(m.schichten.bodenplatte, true), drainage: text(m.schichten.drainage, true),
      umrandung: text(m.schichten.umrandung, true), ueberstand: text(m.schichten.ueberstand, true),
    },
    sichtbar: m.sichtbar,
  };
};

const zuRequest = (f: Form): PoolzeichnungRequest => {
  const teil = (t: Teil) => (t.aktiv ? { breite: mm(t.breite), tiefe: mm(t.tiefe), seite: t.seite } : null);
  return {
    kundeId: f.kundeId,
    datum: f.datum,
    titel: f.titel,
    masse: {
      innenLaenge: mm(f.innenLaenge), innenBreite: mm(f.innenBreite), innenHoehe: mm(f.innenHoehe),
      aussenLaenge: mm(f.aussenLaenge), aussenBreite: mm(f.aussenBreite),
      skimmer: teil(f.skimmer),
      gegenstromanlage: teil(f.gegenstromanlage),
      grube: { oben: mm(f.grube.oben), unten: mm(f.grube.unten), links: mm(f.grube.links), rechts: mm(f.grube.rechts) },
      schichten: {
        poolboden: mm(f.schichten.poolboden), isolierung: mm(f.schichten.isolierung),
        bodenplatte: mm(f.schichten.bodenplatte), drainage: mm(f.schichten.drainage),
        umrandung: mm(f.schichten.umrandung), ueberstand: mm(f.schichten.ueberstand),
      },
      sichtbar: f.sichtbar,
    },
  };
};

const MmFeld: React.FC<{
  label: string; value: string; onChange: (v: string) => void; hinweis?: string; required?: boolean;
}> = ({ label, value, onChange, hinweis, required }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <div className="relative">
      <input type="number" inputMode="numeric" min={0} step={1} required={required} value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={required ? '' : '0'}
        className="w-full p-2 pr-10 border border-gray-300 rounded-lg text-sm" />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">mm</span>
    </div>
    {hinweis && <p className="mt-1 text-xs text-gray-500">{hinweis}</p>}
  </div>
);

const SeitenWahl: React.FC<{ value: Seite; onChange: (s: Seite) => void }> = ({ value, onChange }) => (
  <div>
    <span className="block text-sm font-medium mb-1">Seite</span>
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
      {(['LINKS', 'RECHTS'] as Seite[]).map(s => (
        <button key={s} type="button" onClick={() => onChange(s)} aria-pressed={value === s}
          className={`px-3 py-2 text-sm ${value === s ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
          {s === 'LINKS' ? 'Links' : 'Rechts'}
        </button>
      ))}
    </div>
  </div>
);

const Karte: React.FC<{ titel: string; children: React.ReactNode; zusatz?: React.ReactNode }> = ({ titel, children, zusatz }) => (
  <div className="bg-white rounded-lg shadow p-4 md:p-5">
    <div className="flex items-baseline justify-between gap-2 mb-3">
      <h2 className="text-base font-semibold">{titel}</h2>
      {zusatz}
    </div>
    {children}
  </div>
);

const svgBild = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const PoolzeichnungForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exportiert, setExportiert] = useState<'pdf' | 'png' | null>(null);
  const [form, setForm] = useState<Form>(leer(Number(searchParams.get('kundeId')) || 0));
  const [vorschau, setVorschau] = useState<{ grundriss: string; tiefe: string; requestJson: string } | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);

  useEffect(() => {
    const laden = async () => {
      try {
        const zeichnung = isEditMode ? await poolzeichnungService.getById(Number(id)) : null;
        const kundeId = zeichnung?.kundeId ?? form.kundeId;
        if (!kundeId) throw new Error('Kein Kunde');
        setKunde(await kundeService.getById(kundeId));
        if (zeichnung) setForm(ausMasse(zeichnung.kundeId, zeichnung.datum, zeichnung.titel, zeichnung.masse));
      } catch (err) {
        toast.error(fehlerText(err, 'Poolzeichnung konnte nicht geladen werden'));
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    laden();
    // Lädt einmal je Zeichnung; `form.kundeId` stammt beim Anlegen aus der URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const request = useMemo(() => zuRequest(form), [form]);
  const requestJson = JSON.stringify(request);
  const vorschauAktuell = vorschau?.requestJson === requestJson;
  const grundmasseDa = request.masse.innenLaenge > 0 && request.masse.innenBreite > 0 && request.masse.innenHoehe > 0
    && request.masse.aussenLaenge > 0 && request.masse.aussenBreite > 0;

  // Vorschau kurz nach der letzten Eingabe neu zeichnen lassen; ältere Anfragen werden verworfen.
  useEffect(() => {
    setVorschauFehler(null);
    if (loading || !grundmasseDa) return;
    const abbruch = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const [grundriss, tiefe] = await Promise.all([
          poolzeichnungService.vorschau(request, 'grundriss', abbruch.signal),
          poolzeichnungService.vorschau(request, 'tiefe', abbruch.signal),
        ]);
        if (abbruch.signal.aborted) return;
        setVorschau({ grundriss, tiefe, requestJson });
        setVorschauFehler(null);
      } catch (err) {
        if (!abbruch.signal.aborted) setVorschauFehler(err instanceof Error ? err.message : 'Vorschau nicht möglich');
      }
    }, 350);
    return () => { abbruch.abort(); window.clearTimeout(timer); };
    // `requestJson` fasst `request` zusammen; ein neues Objekt mit gleichem Inhalt soll nicht neu zeichnen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestJson, loading, grundmasseDa]);

  const setze = <K extends keyof Form>(key: K, wert: Form[K]) => setForm(f => ({ ...f, [key]: wert }));

  /** Skimmer und Gegenstromanlage sitzen nie auf derselben Seite: wer kollidiert, wechselt nach gegenüber. */
  const aendereTeil = (name: TeilName, aenderung: Partial<Teil>) => setForm(f => {
    const neu = { ...f[name], ...aenderung };
    const andere = f[anderes(name)];
    if (!andere.aktiv || andere.seite !== neu.seite) return { ...f, [name]: neu };
    // Beim Einschalten weicht das neue Teil aus, beim Seitenwechsel das andere.
    return aenderung.seite
      ? { ...f, [name]: neu, [anderes(name)]: { ...andere, seite: gegenueber(neu.seite) } }
      : { ...f, [name]: { ...neu, seite: gegenueber(andere.seite) } };
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEditMode) {
        await poolzeichnungService.update(Number(id), request);
        markiereNeu(`poolzeichnung-${id}`);
        toast.success('Poolzeichnung gespeichert');
      } else {
        const angelegt = await poolzeichnungService.create(request);
        markiereNeu(`poolzeichnung-${angelegt.id}`);
        toast.success('Poolzeichnung angelegt');
      }
      // Zurück dorthin, woher man kam (meist der Kunde); direkt geöffnet → zum Kunden.
      const idx = (window.history.state as { idx?: number } | null)?.idx;
      if (idx !== undefined && idx > 0) navigate(-1);
      else navigate(`/kunden/${form.kundeId}`, { replace: true });
    } catch (err) {
      toast.error(fehlerText(err, 'Poolzeichnung konnte nicht gespeichert werden'));
    } finally {
      setSubmitting(false);
    }
  };

  const exportiere = async (art: 'pdf' | 'png') => {
    setExportiert(art);
    try {
      if (art === 'pdf') {
        await poolzeichnungService.downloadPdfAusFormular(request);
      } else {
        const name = [kunde?.vorname, kunde?.nachnameFirma].filter(Boolean).join('_');
        await poolzeichnungService.downloadPng(request, `Poolzeichnung_${name}_${form.datum}.png`.replace(/\s+/g, '_'));
      }
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : (art === 'pdf' ? 'PDF' : 'Bild') + ' konnte nicht erzeugt werden');
    } finally {
      setExportiert(null);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!kunde) return null;

  const kundeName = [kunde.vorname, kunde.nachnameFirma].filter(Boolean).join(' ');
  const m = request.masse;
  const wandL = (m.aussenLaenge - m.innenLaenge) / 2;
  const wandB = (m.aussenBreite - m.innenBreite) / 2;
  const wandHinweis = grundmasseDa && wandL >= 0 && wandB >= 0
    ? (wandL === wandB ? `Wandstärke ${wandL} mm` : `Wandstärke ${wandB} mm (Längsseiten), ${wandL} mm (Stirnseiten)`)
    : undefined;
  const abInnen = (abstand: number, wand: number) => (grundmasseDa && wand >= 0 ? `${abstand + wand} mm ab Innenmaß` : undefined);
  const gesamttiefe = m.innenHoehe + m.schichten.poolboden + m.schichten.isolierung + m.schichten.bodenplatte
    + m.schichten.drainage + m.schichten.umrandung - m.schichten.ueberstand;

  const teilKarte = (name: TeilName, titel: string) => {
    const t = form[name];
    return (
      <div className="rounded-lg border border-gray-200 p-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={t.aktiv} onChange={(e) => aendereTeil(name, { aktiv: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-brand-600" />
          {titel}
        </label>
        {t.aktiv && (
          <div className="ui-aufklappen grid grid-cols-2 gap-3 mt-3">
            <MmFeld label="Breite" value={t.breite} onChange={(v) => aendereTeil(name, { breite: v })} required
              hinweis="entlang der Wand" />
            <MmFeld label="Tiefe" value={t.tiefe} onChange={(v) => aendereTeil(name, { tiefe: v })} required
              hinweis="ab Innenmaß nach außen" />
            <SeitenWahl value={t.seite} onChange={(s) => aendereTeil(name, { seite: s })} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-3">
        <ZurueckLink fallback={`/kunden/${kunde.id}`} fallbackLabel="Zurück zum Kunden" />
      </div>
      <PageHeader title={isEditMode ? 'Poolzeichnung bearbeiten' : 'Poolzeichnung anlegen'} subtitle={kundeName} />

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] gap-6 items-start">
        <div className="space-y-4">
          <Karte titel="Allgemein">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Titel</label>
                <input type="text" required value={form.titel} onChange={(e) => setze('titel', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Datum</label>
                <DatumFeld required value={form.datum} onChange={(v) => setze('datum', v)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
          </Karte>

          <Karte titel="Innenmaß">
            <div className="grid grid-cols-3 gap-3">
              <MmFeld label="Länge" value={form.innenLaenge} onChange={(v) => setze('innenLaenge', v)} required />
              <MmFeld label="Breite" value={form.innenBreite} onChange={(v) => setze('innenBreite', v)} required />
              <MmFeld label="Höhe" value={form.innenHoehe} onChange={(v) => setze('innenHoehe', v)} required />
            </div>
          </Karte>

          <Karte titel="Außenmaß">
            <div className="grid grid-cols-2 gap-3">
              <MmFeld label="Länge" value={form.aussenLaenge} onChange={(v) => setze('aussenLaenge', v)} required />
              <MmFeld label="Breite" value={form.aussenBreite} onChange={(v) => setze('aussenBreite', v)} required />
            </div>
            {wandHinweis && <p className="mt-2 text-xs text-gray-500">{wandHinweis}</p>}
          </Karte>

          <Karte titel="Einbauteile">
            <p className="text-xs text-gray-500 mb-3">
              Sitzen mittig an einer Stirnseite, nie beide auf derselben. Ragen sie über die Wand hinaus, wächst nur das Außenmaß.
            </p>
            <div className="space-y-3">
              {teilKarte('skimmer', 'Skimmer')}
              {teilKarte('gegenstromanlage', 'Gegenstromanlage')}
            </div>
          </Karte>

          <Karte titel="Baugrube" zusatz={<span className="text-xs text-gray-500">Abstand zum Außenmaß</span>}>
            <div className="grid grid-cols-2 gap-3">
              <MmFeld label="Oben" value={form.grube.oben} onChange={(v) => setze('grube', { ...form.grube, oben: v })}
                hinweis={abInnen(m.grube.oben, wandB)} />
              <MmFeld label="Unten" value={form.grube.unten} onChange={(v) => setze('grube', { ...form.grube, unten: v })}
                hinweis={abInnen(m.grube.unten, wandB)} />
              <MmFeld label="Links" value={form.grube.links} onChange={(v) => setze('grube', { ...form.grube, links: v })}
                hinweis={abInnen(m.grube.links, wandL)} />
              <MmFeld label="Rechts" value={form.grube.rechts} onChange={(v) => setze('grube', { ...form.grube, rechts: v })}
                hinweis={abInnen(m.grube.rechts, wandL)} />
            </div>
          </Karte>

          <Karte titel="Tiefe der Baugrube"
            zusatz={<span className="text-sm text-gray-500">Gesamt <span className="font-semibold text-brand-800">{gesamttiefe} mm</span></span>}>
            <p className="text-xs text-gray-500 mb-3">Poolinnenmaß (Höhe {m.innenHoehe || 0} mm) plus diese Schichten, abzüglich Überstand.</p>
            <div className="grid grid-cols-2 gap-3">
              <MmFeld label="Poolboden" value={form.schichten.poolboden}
                onChange={(v) => setze('schichten', { ...form.schichten, poolboden: v })} />
              <MmFeld label="Isolierung" value={form.schichten.isolierung}
                onChange={(v) => setze('schichten', { ...form.schichten, isolierung: v })} />
              <MmFeld label="Bodenplatte" value={form.schichten.bodenplatte}
                onChange={(v) => setze('schichten', { ...form.schichten, bodenplatte: v })} />
              <MmFeld label="Drainageschicht" value={form.schichten.drainage}
                onChange={(v) => setze('schichten', { ...form.schichten, drainage: v })} />
              <MmFeld label="Poolumrandung" value={form.schichten.umrandung}
                onChange={(v) => setze('schichten', { ...form.schichten, umrandung: v })} />
              <MmFeld label="Überstand über Gelände" value={form.schichten.ueberstand}
                onChange={(v) => setze('schichten', { ...form.schichten, ueberstand: v })}
                hinweis="Oberkante Umrandung über Gelände" />
            </div>
          </Karte>
        </div>

        <div className="lg:sticky lg:top-4 space-y-4">
          <div className="bg-white rounded-lg shadow p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Maße auf der Zeichnung</span>
                {([['innen', 'Innen'], ['aussen', 'Außen'], ['grube', 'Grube']] as const).map(([key, label]) => (
                  <button key={key} type="button" aria-pressed={form.sichtbar[key]}
                    onClick={() => setze('sichtbar', { ...form.sichtbar, [key]: !form.sichtbar[key] })}
                    className={`rounded-full border px-3 py-1 text-sm ${form.sichtbar[key] ? filterAktiv : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => exportiere('pdf')} disabled={!grundmasseDa || !vorschauAktuell || !!vorschauFehler || !!exportiert}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-sm text-brand-700 hover:bg-brand-50 disabled:opacity-50">
                  {exportiert === 'pdf' ? <Spinner className="h-4 w-4" /> : <Icon.Download className="h-4 w-4" />}PDF
                </button>
                <button type="button" onClick={() => exportiere('png')} disabled={!grundmasseDa || !vorschauAktuell || !!vorschauFehler || !!exportiert}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-sm text-brand-700 hover:bg-brand-50 disabled:opacity-50">
                  {exportiert === 'png' ? <Spinner className="h-4 w-4" /> : <Icon.Bild className="h-4 w-4" />}Bild
                </button>
              </div>
            </div>

            {vorschauFehler && grundmasseDa && (
              <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{vorschauFehler}</p>
            )}
            {!grundmasseDa ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-16 text-center">
                <Icon.Grundriss className="h-8 w-8 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Innen- und Außenmaß eingeben, dann erscheint hier die Zeichnung.</p>
              </div>
            ) : vorschau && vorschauAktuell ? (
              // Eine veraltete Zeichnung bleibt nie stehen; die neue blendet weich ein, statt aufzuspringen.
              <div key={vorschau.requestJson} className={`ui-einblenden space-y-4 transition-opacity ${vorschauFehler ? 'opacity-40' : ''}`}>
                <img src={svgBild(vorschau.grundriss)} alt="Grundriss" className="w-full h-auto" />
                <div>
                  <h3 className="text-sm font-semibold text-brand-800 mb-1">Tiefe der Baugrube</h3>
                  <img src={svgBild(vorschau.tiefe)} alt="Tiefe der Baugrube" className="w-full h-auto" />
                </div>
              </div>
            ) : vorschauFehler ? (
              <p className="py-12 text-center text-sm text-gray-500">Bitte die Angaben korrigieren, um die Zeichnung anzuzeigen.</p>
            ) : (
              <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>
            )}
          </div>

          <div className="flex justify-end gap-4">
            <button type="button" onClick={() => navigate(-1)}
              className="px-6 py-3 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">
              Abbrechen
            </button>
            <button type="submit" disabled={submitting}
              className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
              {submitting ? 'Wird gespeichert …' : isEditMode ? 'Speichern' : 'Poolzeichnung anlegen'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PoolzeichnungForm;
